import React from "react";
import Image from "@/lib/next-shims/next-image";
import {
  Bell,
  Bot,
  Cloud,
  Database,
  FileSpreadsheet,
  Globe,
  KeyRound,
  Lock,
  Send,
  Rows3,
  Settings,
  type LucideIcon,
} from "lucide-react";

/** Database subtypes supported */
export const databaseSubtypes = [
  "postgres",
  "mysql",
  "bigquery",
  "d1",
  "mongodb",
  "sqlserver",
] as const;

export type ConnectorCapability =
  | "database"
  | "sheet"
  | "key_value"
  | "endpoint"
  | "secret"
  | "notification"
  | "storage";

export type ConnectorAction = "test" | "browse" | "query";

type ConnectorCapabilitySource = {
  type?: string | null;
  subtype?: string | null;
  configUiSchema?: Record<string, unknown> | null;
};

const connectorCapabilities = [
  "database",
  "sheet",
  "key_value",
  "endpoint",
  "secret",
  "notification",
  "storage",
] as const satisfies readonly ConnectorCapability[];

const legacySubtypeCapabilities: Record<string, ConnectorCapability> = {
  postgres: "database",
  mysql: "database",
  bigquery: "database",
  d1: "database",
  mongodb: "database",
  sqlserver: "database",
  google_sheets: "sheet",
  redis: "key_value",
  http_request: "endpoint",
  bearer_token: "secret",
  api_key_header: "secret",
  basic_auth: "secret",
  custom_secret: "secret",
  lark_webhook: "notification",
  lark_bot: "notification",
  telegram_bot: "notification",
  google_drive: "storage",
  s3: "storage",
  r2: "storage",
  gcs: "storage",
};

const defaultActionsByCapability: Record<ConnectorCapability, ConnectorAction[]> = {
  database: ["test", "browse", "query"],
  sheet: ["test", "browse"],
  key_value: ["test", "browse"],
  endpoint: [],
  secret: [],
  notification: [],
  storage: ["test", "browse"],
};

function isConnectorCapability(value: unknown): value is ConnectorCapability {
  return typeof value === "string" && connectorCapabilities.includes(value as ConnectorCapability);
}

function normalizeActionList(value: unknown): ConnectorAction[] | null {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is ConnectorAction => item === "test" || item === "browse" || item === "query",
    );
  }

  if (value && typeof value === "object") {
    return (["test", "browse", "query"] as const).filter(
      (action) => (value as Record<string, unknown>)[action] === true,
    );
  }

  return null;
}

export function resolveConnectorCapability(
  source?: ConnectorCapabilitySource | string | null,
): ConnectorCapability | null {
  const subtype = typeof source === "string" ? source : source?.subtype || null;
  const schema = typeof source === "string" ? null : source?.configUiSchema || null;

  const schemaCapability = schema?.xCapability;
  if (isConnectorCapability(schemaCapability)) {
    return schemaCapability;
  }

  if (subtype && legacySubtypeCapabilities[subtype]) {
    return legacySubtypeCapabilities[subtype];
  }

  const type = typeof source === "string" ? null : source?.type || null;
  if (type === "endpoint") return "endpoint";
  if (type === "secret_key") return "secret";
  if (type === "notification") return "notification";
  if (type === "storage") return "storage";

  return null;
}

export function resolveConnectorActions(
  source?: ConnectorCapabilitySource | string | null,
): Set<ConnectorAction> {
  if (typeof source !== "string") {
    const explicitActions = normalizeActionList(source?.configUiSchema?.xActions);
    if (explicitActions) {
      return new Set(explicitActions);
    }
  }

  const capability = resolveConnectorCapability(source);
  return new Set(capability ? defaultActionsByCapability[capability] : []);
}

export function connectorHasAction(
  source: ConnectorCapabilitySource | string | null | undefined,
  action: ConnectorAction,
): boolean {
  return resolveConnectorActions(source).has(action);
}

export function isDatabaseSubtype(subtype?: string | null): boolean {
  return resolveConnectorCapability(subtype) === "database";
}

/** Subtype display config */
export const subtypeConfig: Record<string, { label: string; shortLabel: string; color: string }> = {
  // Credential (Database)
  postgres: { label: "PostgreSQL", shortLabel: "PG", color: "text-blue-600" },
  mysql: { label: "MySQL", shortLabel: "MySQL", color: "text-orange-600" },
  bigquery: { label: "BigQuery", shortLabel: "BQ", color: "text-indigo-600" },
  d1: { label: "Cloudflare D1", shortLabel: "D1", color: "text-amber-600" },
  mongodb: { label: "MongoDB", shortLabel: "Mongo", color: "text-green-600" },
  sqlserver: { label: "SQL Server", shortLabel: "SQL", color: "text-red-600" },
  google_sheets: { label: "Google Sheets", shortLabel: "Sheets", color: "text-green-600" },
  redis: { label: "Redis", shortLabel: "Redis", color: "text-red-600" },
  google_drive: { label: "Google Drive", shortLabel: "Drive", color: "text-sky-600" },
  s3: { label: "AWS S3", shortLabel: "S3", color: "text-orange-600" },
  r2: { label: "Cloudflare R2", shortLabel: "R2", color: "text-orange-600" },
  gcs: { label: "Google Cloud Storage", shortLabel: "GCS", color: "text-blue-600" },
  // Endpoint
  http_request: { label: "HTTP Request", shortLabel: "HTTP", color: "text-cyan-600" },
  // Secret Key
  bearer_token: { label: "Bearer Token", shortLabel: "Bearer", color: "text-emerald-600" },
  api_key_header: { label: "API Key (Header)", shortLabel: "API Key", color: "text-violet-600" },
  basic_auth: { label: "Basic Auth", shortLabel: "Basic", color: "text-rose-600" },
  custom_secret: { label: "Custom Secret", shortLabel: "Custom", color: "text-gray-600" },
  // Notification
  lark_webhook: { label: "Lark Webhook", shortLabel: "Lark WH", color: "text-blue-600" },
  lark_bot: { label: "Lark Bot", shortLabel: "Lark Bot", color: "text-blue-600" },
  telegram_bot: { label: "Telegram Bot", shortLabel: "Telegram", color: "text-cyan-600" },
};

/** Lucide icon + color for non-database subtypes (fallback when no brand logo) */
const subtypeIcons: Record<
  string,
  { icon: LucideIcon; bgColor: string; darkBgColor: string; iconColor: string }
> = {
  http_request: {
    icon: Globe,
    bgColor: "bg-cyan-50",
    darkBgColor: "dark:bg-cyan-950/40",
    iconColor: "text-cyan-600 dark:text-cyan-400",
  },
  bearer_token: {
    icon: KeyRound,
    bgColor: "bg-emerald-50",
    darkBgColor: "dark:bg-emerald-950/40",
    iconColor: "text-emerald-600 dark:text-emerald-400",
  },
  api_key_header: {
    icon: KeyRound,
    bgColor: "bg-violet-50",
    darkBgColor: "dark:bg-violet-950/40",
    iconColor: "text-violet-600 dark:text-violet-400",
  },
  basic_auth: {
    icon: Lock,
    bgColor: "bg-rose-50",
    darkBgColor: "dark:bg-rose-950/40",
    iconColor: "text-rose-600 dark:text-rose-400",
  },
  custom_secret: {
    icon: Settings,
    bgColor: "bg-gray-50",
    darkBgColor: "dark:bg-gray-800",
    iconColor: "text-gray-600 dark:text-gray-400",
  },
  // Notification
  lark_webhook: {
    icon: Bell,
    bgColor: "bg-blue-50",
    darkBgColor: "dark:bg-blue-950/40",
    iconColor: "text-blue-600 dark:text-blue-400",
  },
  lark_bot: {
    icon: Bot,
    bgColor: "bg-blue-50",
    darkBgColor: "dark:bg-blue-950/40",
    iconColor: "text-blue-600 dark:text-blue-400",
  },
  telegram_bot: {
    icon: Send,
    bgColor: "bg-cyan-50",
    darkBgColor: "dark:bg-cyan-950/40",
    iconColor: "text-cyan-600 dark:text-cyan-400",
  },
  google_sheets: {
    icon: FileSpreadsheet,
    bgColor: "bg-green-50",
    darkBgColor: "dark:bg-green-950/40",
    iconColor: "text-green-600 dark:text-green-400",
  },
  redis: {
    icon: Rows3,
    bgColor: "bg-red-50",
    darkBgColor: "dark:bg-red-950/40",
    iconColor: "text-red-600 dark:text-red-400",
  },
  google_drive: {
    icon: Cloud,
    bgColor: "bg-sky-50",
    darkBgColor: "dark:bg-sky-950/40",
    iconColor: "text-sky-600 dark:text-sky-400",
  },
  s3: {
    icon: Cloud,
    bgColor: "bg-orange-50",
    darkBgColor: "dark:bg-orange-950/40",
    iconColor: "text-orange-600 dark:text-orange-400",
  },
  r2: {
    icon: Cloud,
    bgColor: "bg-orange-50",
    darkBgColor: "dark:bg-orange-950/40",
    iconColor: "text-orange-600 dark:text-orange-400",
  },
  gcs: {
    icon: Cloud,
    bgColor: "bg-blue-50",
    darkBgColor: "dark:bg-blue-950/40",
    iconColor: "text-blue-600 dark:text-blue-400",
  },
};

/** Get label for a subtype */
export function getSubtypeLabel(subtype: string): string {
  return subtypeConfig[subtype]?.label || subtype;
}

// Size configurations
const sizeConfig = {
  sm: { container: "w-6 h-6", icon: 14, padding: "p-1" },
  md: { container: "w-8 h-8", icon: 18, padding: "p-1.5" },
  lg: { container: "w-10 h-10", icon: 22, padding: "p-2" },
};

// Brand logo configurations with CDN URLs
const subtypeLogos: Record<
  string,
  {
    logo: string;
    bgColor: string;
    darkBgColor: string;
  }
> = {
  postgres: {
    logo: "/connector-logos/postgres.svg",
    bgColor: "bg-blue-50",
    darkBgColor: "dark:bg-blue-950/40",
  },
  mysql: {
    logo: "/connector-logos/mysql.svg",
    bgColor: "bg-blue-50",
    darkBgColor: "dark:bg-blue-950/40",
  },
  bigquery: {
    logo: "/connector-logos/bigquery.svg",
    bgColor: "bg-indigo-50",
    darkBgColor: "dark:bg-indigo-950/40",
  },
  d1: {
    logo: "/connector-logos/cloudflare.svg",
    bgColor: "bg-orange-50",
    darkBgColor: "dark:bg-orange-950/40",
  },
  mongodb: {
    logo: "/connector-logos/mongodb.svg",
    bgColor: "bg-green-50",
    darkBgColor: "dark:bg-green-950/40",
  },
};

/** Subtype icon component with brand logos */
export function SubtypeIcon({
  subtype,
  className,
  size = "md",
}: {
  subtype: string;
  className?: string;
  size?: "sm" | "md" | "lg";
}) {
  const sizeConf = sizeConfig[size];

  // CDN brand logo
  const config = subtypeLogos[subtype];
  if (config) {
    return (
      <div
        className={`inline-flex items-center justify-center rounded-lg ${sizeConf.container} ${sizeConf.padding} ${config.bgColor} ${config.darkBgColor} ${className || ""}`}
      >
        <Image
          src={config.logo}
          alt={getSubtypeLabel(subtype)}
          width={sizeConf.icon}
          height={sizeConf.icon}
          className="object-contain"
          unoptimized
        />
      </div>
    );
  }

  // Lucide icon for non-database subtypes
  const iconConf = subtypeIcons[subtype];
  if (iconConf) {
    const Icon = iconConf.icon;
    return (
      <div
        className={`inline-flex items-center justify-center rounded-lg ${sizeConf.container} ${sizeConf.padding} ${iconConf.bgColor} ${iconConf.darkBgColor} ${className || ""}`}
      >
        <Icon size={sizeConf.icon} className={iconConf.iconColor} />
      </div>
    );
  }

  // Generic fallback
  return (
    <div
      className={`inline-flex items-center justify-center rounded-lg ${sizeConf.container} ${sizeConf.padding} bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 ${className || ""}`}
    >
      <Database size={sizeConf.icon} />
    </div>
  );
}

/** Subtype groups for toggle filter */
export interface SubtypeGroup {
  id: string;
  label: string;
  subtypes: string[];
  icon: React.ReactNode;
}

/** Returns subtype groups with localised labels. Pass t from useTranslations("connectors"). */
export function getSubtypeGroups(allLabel: string): SubtypeGroup[] {
  return [
    {
      id: "all",
      label: allLabel,
      subtypes: [],
      icon: <Database className="size-4" />,
    },
    {
      id: "postgres",
      label: "PostgreSQL",
      subtypes: ["postgres"],
      icon: (
        <Image
          src="/connector-logos/postgres.svg"
          alt="PostgreSQL"
          width={16}
          height={16}
          className="object-contain"
          unoptimized
        />
      ),
    },
    {
      id: "mysql",
      label: "MySQL",
      subtypes: ["mysql"],
      icon: (
        <Image
          src="/connector-logos/mysql.svg"
          alt="MySQL"
          width={16}
          height={16}
          className="object-contain"
          unoptimized
        />
      ),
    },
    {
      id: "bigquery",
      label: "BigQuery",
      subtypes: ["bigquery"],
      icon: (
        <Image
          src="/connector-logos/bigquery.svg"
          alt="BigQuery"
          width={16}
          height={16}
          className="object-contain"
          unoptimized
        />
      ),
    },
    {
      id: "d1",
      label: "D1",
      subtypes: ["d1"],
      icon: (
        <Image
          src="/connector-logos/cloudflare.svg"
          alt="Cloudflare D1"
          width={16}
          height={16}
          className="object-contain"
          unoptimized
        />
      ),
    },
    {
      id: "mongodb",
      label: "MongoDB",
      subtypes: ["mongodb"],
      icon: (
        <Image
          src="/connector-logos/mongodb.svg"
          alt="MongoDB"
          width={16}
          height={16}
          className="object-contain"
          unoptimized
        />
      ),
    },
    {
      id: "sqlserver",
      label: "SQL Server",
      subtypes: ["sqlserver"],
      icon: <Database className="size-4" />,
    },
    {
      id: "google_sheets",
      label: "Google Sheets",
      subtypes: ["google_sheets"],
      icon: <FileSpreadsheet className="size-4" />,
    },
    {
      id: "redis",
      label: "Redis",
      subtypes: ["redis"],
      icon: <Rows3 className="size-4" />,
    },
    {
      id: "google_drive",
      label: "Google Drive",
      subtypes: ["google_drive"],
      icon: <Cloud className="size-4" />,
    },
    {
      id: "s3",
      label: "AWS S3",
      subtypes: ["s3"],
      icon: <Cloud className="size-4" />,
    },
    {
      id: "r2",
      label: "Cloudflare R2",
      subtypes: ["r2"],
      icon: <Cloud className="size-4" />,
    },
    {
      id: "gcs",
      label: "Google Cloud Storage",
      subtypes: ["gcs"],
      icon: <Cloud className="size-4" />,
    },
    {
      id: "http_request",
      label: "HTTP",
      subtypes: ["http_request"],
      icon: <Globe className="size-4" />,
    },
    {
      id: "bearer_token",
      label: "Bearer",
      subtypes: ["bearer_token"],
      icon: <KeyRound className="size-4" />,
    },
    {
      id: "api_key_header",
      label: "API Key",
      subtypes: ["api_key_header"],
      icon: <KeyRound className="size-4" />,
    },
    {
      id: "basic_auth",
      label: "Basic Auth",
      subtypes: ["basic_auth"],
      icon: <Lock className="size-4" />,
    },
    {
      id: "custom_secret",
      label: "Secret",
      subtypes: ["custom_secret"],
      icon: <Settings className="size-4" />,
    },
    {
      id: "lark_webhook",
      label: "Lark Webhook",
      subtypes: ["lark_webhook"],
      icon: <Bell className="size-4" />,
    },
    {
      id: "lark_bot",
      label: "Lark Bot",
      subtypes: ["lark_bot"],
      icon: <Bot className="size-4" />,
    },
    {
      id: "telegram_bot",
      label: "Telegram",
      subtypes: ["telegram_bot"],
      icon: <Send className="size-4" />,
    },
  ];
}
